import { createAction, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosPrivate } from '../hooks/axiosInstance';

// Upload media files
export const uploadMedia = createAsyncThunk(
  'media/uploadMedia',
  async (formData, thunkAPI) => {
    try {
      const response = await axiosPrivate.post('/admin/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get all media for user with filters
export const getUserMedia = createAsyncThunk(
  'media/getUserMedia',
  async (params = {}, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.entityType && params.entityType !== 'all') queryParams.append('entityType', params.entityType);
      if (params.mediaType && params.mediaType !== 'all') queryParams.append('mediaType', params.mediaType);
      if (params.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await axiosPrivate.get(`/admin/media?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get media for specific entity
export const getEntityMedia = createAsyncThunk(
  'media/getEntityMedia',
  async ({ entityType, entityId, params = {} }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.mediaType && params.mediaType !== 'all') queryParams.append('mediaType', params.mediaType);
      if (params.status) queryParams.append('status', params.status);

      const response = await axiosPrivate.get(`/admin/media/${entityType}/${entityId}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Update media details
export const updateMedia = createAsyncThunk(
  'media/updateMedia',
  async ({ id, data }, thunkAPI) => {
    try {
      const response = await axiosPrivate.put(`/admin/media/${id}`, data);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Delete media (soft or permanent)
export const deleteMedia = createAsyncThunk(
  'media/deleteMedia',
  async ({ id, permanent = false }, thunkAPI) => {
    try {
      const response = await axiosPrivate.delete(`/admin/media/${id}?permanent=${permanent}`);
      return { id, permanent, message: response.data.msg };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Restore deleted media
export const restoreMedia = createAsyncThunk(
  'media/restoreMedia',
  async (id, thunkAPI) => {
    try {
      const response = await axiosPrivate.patch(`/admin/media/${id}/restore`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { msg: error.message });
    }
  }
);

// Reorder media
export const reorderMedia = createAsyncThunk(
  'media/reorderMedia',
  async ({ entityType, entityId, mediaIds }, thunkAPI) => {
    try {
      const response = await axiosPrivate.put(`/admin/media/${entityType}/${entityId}/reorder`, {
        mediaIds
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Delete all media for entity
export const deleteEntityMedia = createAsyncThunk(
  'media/deleteEntityMedia',
  async ({ entityType, entityId, permanent = false }, thunkAPI) => {
    try {
      const response = await axiosPrivate.delete(`/admin/media/${entityType}/${entityId}?permanent=${permanent}`);
      return { entityType, entityId, permanent, message: response.data.msg };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Get media statistics
export const getMediaStats = createAsyncThunk(
  'media/getMediaStats',
  async (_, thunkAPI) => {
    try {
      const response = await axiosPrivate.get('/admin/media-stats');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// ========== NEW FEATURED/COVER IMAGE FUNCTIONS ==========

// Get featured/cover image for an entity
export const getFeaturedImage = createAsyncThunk(
  'media/getFeaturedImage',
  async ({ entityType, entityId }, thunkAPI) => {
    try {
      const response = await axiosPrivate.get(`/admin/featured-image/${entityType}/${entityId}`);
      return { ...response.data, entityType, entityId };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Update featured/cover image for an entity
export const updateFeaturedImage = createAsyncThunk(
  'media/updateFeaturedImage',
  async ({ entityType, entityId, formData }, thunkAPI) => {
    try {
      const response = await axiosPrivate.put(`/admin/featured-image/${entityType}/${entityId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return { ...response.data, entityType, entityId };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Delete featured/cover image for an entity
export const deleteFeaturedImage = createAsyncThunk(
  'media/deleteFeaturedImage',
  async ({ entityType, entityId }, thunkAPI) => {
    try {
      const response = await axiosPrivate.delete(`/admin/featured-image/${entityType}/${entityId}`);
      return { ...response.data, entityType, entityId };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

// Clear error action
export const clearError = createAction('media/clearError');

// Clear success message action
export const clearSuccess = createAction('media/clearSuccess');

export const mediaSlice = createSlice({
  name: 'media',
  initialState: {
    media: [],
    entityMedia: {},
    featuredImages: {}, // Store featured/cover images by entityType_entityId
    stats: {},
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0
    },
    filters: {
      entityType: 'all',
      mediaType: 'all',
      status: 'active',
      search: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    },
    loading: false,
    uploading: false,
    processing: false,
    featuredImageLoading: false,
    featuredImageUploading: false,
    error: null,
    success: null,
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearMedia: (state) => {
      state.media = [];
      state.entityMedia = {};
    },
    clearEntityMedia: (state, action) => {
      const { entityType, entityId } = action.payload;
      const key = `${entityType}_${entityId}`;
      delete state.entityMedia[key];
    },
    clearFeaturedImage: (state, action) => {
      const { entityType, entityId } = action.payload;
      const key = `${entityType}_${entityId}`;
      delete state.featuredImages[key];
    },
    clearAllFeaturedImages: (state) => {
      state.featuredImages = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload Media
      .addCase(uploadMedia.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadMedia.fulfilled, (state, action) => {
        state.uploading = false;
        state.success = action.payload.msg;
        // Add new media to the state if it matches current filters
        if (action.payload.media) {
          state.media = [...action.payload.media, ...state.media];
        }
      })
      .addCase(uploadMedia.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get User Media
      .addCase(getUserMedia.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserMedia.fulfilled, (state, action) => {
        state.loading = false;
        state.media = action.payload.media;
        state.pagination = {
          current: action.payload.currentPage,
          pageSize: state.pagination.pageSize,
          total: action.payload.total,
          totalPages: action.payload.totalPages
        };
      })
      .addCase(getUserMedia.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get Entity Media
      .addCase(getEntityMedia.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEntityMedia.fulfilled, (state, action) => {
        state.loading = false;
        // Store entity media with a key format: entityType_entityId
        const key = `${action.meta.arg.entityType}_${action.meta.arg.entityId}`;
        state.entityMedia[key] = action.payload.media;
      })
      .addCase(getEntityMedia.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Update Media
      .addCase(updateMedia.pending, (state) => {
        state.processing = true;
        state.error = null;
      })
      .addCase(updateMedia.fulfilled, (state, action) => {
        state.processing = false;
        state.success = 'Media updated successfully';
        
        // Update media in the main media array
        const index = state.media.findIndex(media => media._id === action.payload._id);
        if (index !== -1) {
          state.media[index] = action.payload;
        }

        // Update media in entity media objects
        Object.keys(state.entityMedia).forEach(key => {
          const entityIndex = state.entityMedia[key].findIndex(media => media._id === action.payload._id);
          if (entityIndex !== -1) {
            state.entityMedia[key][entityIndex] = action.payload;
          }
        });
      })
      .addCase(updateMedia.rejected, (state, action) => {
        state.processing = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Delete Media
      .addCase(deleteMedia.pending, (state) => {
        state.processing = true;
        state.error = null;
      })
      .addCase(deleteMedia.fulfilled, (state, action) => {
        state.processing = false;
        state.success = action.payload.message;
        
        if (action.payload.permanent) {
          // Remove from all arrays if permanently deleted
          state.media = state.media.filter(media => media._id !== action.payload.id);
          Object.keys(state.entityMedia).forEach(key => {
            state.entityMedia[key] = state.entityMedia[key].filter(media => media._id !== action.payload.id);
          });
        } else {
          // Update status to deleted if soft deleted
          const updateStatus = (mediaArray) => {
            const index = mediaArray.findIndex(media => media._id === action.payload.id);
            if (index !== -1) {
              mediaArray[index].status = 'deleted';
            }
          };
          
          updateStatus(state.media);
          Object.keys(state.entityMedia).forEach(key => {
            updateStatus(state.entityMedia[key]);
          });
        }
      })
      .addCase(deleteMedia.rejected, (state, action) => {
        state.processing = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Restore Media
      .addCase(restoreMedia.pending, (state) => {
        state.processing = true;
        state.error = null;
      })
      .addCase(restoreMedia.fulfilled, (state, action) => {
        state.processing = false;
        state.success = action.payload.msg || 'Media restored successfully';
        
        // Update status to active - handle different response formats
        const restoredMedia = action.payload.media || action.payload;
        const mediaId = restoredMedia._id || action.meta.arg;
        
        const updateStatus = (mediaArray) => {
          const index = mediaArray.findIndex(media => media._id === mediaId);
          if (index !== -1) {
            if (restoredMedia._id) {
              // If we have the full media object, use it
              mediaArray[index] = restoredMedia;
            } else {
              // Otherwise just update the status
              mediaArray[index].status = 'active';
            }
          }
        };
        
        updateStatus(state.media);
        Object.keys(state.entityMedia).forEach(key => {
          updateStatus(state.entityMedia[key]);
        });
      })
      .addCase(restoreMedia.rejected, (state, action) => {
        state.processing = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Reorder Media
      .addCase(reorderMedia.pending, (state) => {
        state.processing = true;
        state.error = null;
      })
      .addCase(reorderMedia.fulfilled, (state, action) => {
        state.processing = false;
        state.success = action.payload.msg;
        
        // Update the order in entityMedia
        const { entityType, entityId } = action.meta.arg;
        const key = `${entityType}_${entityId}`;
        if (state.entityMedia[key] && action.payload.media) {
          state.entityMedia[key] = action.payload.media;
        }
      })
      .addCase(reorderMedia.rejected, (state, action) => {
        state.processing = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Delete Entity Media
      .addCase(deleteEntityMedia.pending, (state) => {
        state.processing = true;
        state.error = null;
      })
      .addCase(deleteEntityMedia.fulfilled, (state, action) => {
        state.processing = false;
        state.success = action.payload.message;
        
        const { entityType, entityId } = action.payload;
        const key = `${entityType}_${entityId}`;
        
        if (action.payload.permanent) {
          // Remove all media for this entity
          delete state.entityMedia[key];
          state.media = state.media.filter(media => 
            !(media.entityType === entityType && media.entityId === entityId)
          );
        } else {
          // Mark as deleted
          if (state.entityMedia[key]) {
            state.entityMedia[key] = state.entityMedia[key].map(media => ({
              ...media,
              status: 'deleted'
            }));
          }
          
          state.media = state.media.map(media => {
            if (media.entityType === entityType && media.entityId === entityId) {
              return { ...media, status: 'deleted' };
            }
            return media;
          });
        }
      })
      .addCase(deleteEntityMedia.rejected, (state, action) => {
        state.processing = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Get Media Stats
      .addCase(getMediaStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMediaStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(getMediaStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // ========== FEATURED/COVER IMAGE REDUCERS ==========

      // Get Featured Image
      .addCase(getFeaturedImage.pending, (state) => {
        state.featuredImageLoading = true;
        state.error = null;
      })
      .addCase(getFeaturedImage.fulfilled, (state, action) => {
        state.featuredImageLoading = false;
        const { entityType, entityId } = action.payload;
        const key = `${entityType}_${entityId}`;
        state.featuredImages[key] = action.payload.featuredImage;
      })
      .addCase(getFeaturedImage.rejected, (state, action) => {
        state.featuredImageLoading = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Update Featured Image
      .addCase(updateFeaturedImage.pending, (state) => {
        state.featuredImageUploading = true;
        state.error = null;
      })
      .addCase(updateFeaturedImage.fulfilled, (state, action) => {
        state.featuredImageUploading = false;
        state.success = action.payload.message;
        const { entityType, entityId } = action.payload;
        const key = `${entityType}_${entityId}`;
        state.featuredImages[key] = action.payload.featuredImage;
      })
      .addCase(updateFeaturedImage.rejected, (state, action) => {
        state.featuredImageUploading = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Delete Featured Image
      .addCase(deleteFeaturedImage.pending, (state) => {
        state.processing = true;
        state.error = null;
      })
      .addCase(deleteFeaturedImage.fulfilled, (state, action) => {
        state.processing = false;
        state.success = action.payload.message;
        const { entityType, entityId } = action.payload;
        const key = `${entityType}_${entityId}`;
        state.featuredImages[key] = null;
      })
      .addCase(deleteFeaturedImage.rejected, (state, action) => {
        state.processing = false;
        state.error = action.payload ? action.payload.msg : action.error.message;
      })

      // Clear actions
      .addCase(clearError, (state) => {
        state.error = null;
      })
      .addCase(clearSuccess, (state) => {
        state.success = null;
      });
  },
});

export const { 
  setFilters, 
  setPagination, 
  clearMedia, 
  clearEntityMedia,
  clearFeaturedImage,
  clearAllFeaturedImages
} = mediaSlice.actions;

export default mediaSlice.reducer;